import { useContext, useEffect, useState } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import {
  Braces,
  ChevronDown,
  Database,
  LayoutTemplate,
  Server,
  Sparkles,
  Table2,
  Wrench,
  Zap,
} from "lucide-react";
import type { ComponentType, ReactNode } from "react";
import type {
  MemoryModifier,
  MemoryScopeModifier,
  MemoryTempModifier,
  MentionKind,
  MentionResolution,
  TableModifier,
} from "@cometchat-ai/core";

import { cn } from "../../lib/utils.js";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover.js";
import { MentionResolutionsContext } from "./resolution-context.js";
import { KindIconsContext } from "./kind-icons-context.js";
import {
  SkillActionsContext,
  type DecisionPolicyConfig,
  type SkillActionOption,
} from "./skill-actions-context.js";

/** Per-kind chip glyph — mirrors the `#` popup's category icons for consistency. */
const KIND_ICON: Record<MentionKind, ComponentType<{ className?: string }>> = {
  skill: Wrench,
  tool: Wrench,
  kb: Database,
  table: Table2,
  query: Server,
  context: Braces,
  memory: Sparkles,
  card: LayoutTemplate,
  action: Zap,
};

function tokenOf(kind: MentionKind, slug: string): string {
  return `#${kind === "tool" ? "skill" : kind}/${slug}`;
}

/** Human noun per kind, for the broken-mention tooltip. */
const KIND_NOUN: Record<MentionKind, string> = {
  skill: "Tool",
  tool: "Tool",
  kb: "Knowledge base",
  table: "Table",
  query: "Query",
  context: "Variable",
  memory: "Variable",
  card: "Card",
  action: "Action",
};

/** The table permission ladder (narrative §4). `null` = SELECT-only default. */
export const TABLE_ACCESS_LEVELS: {
  value: TableModifier | null;
  label: string;
  detail: string;
  write: boolean;
}[] = [
  { value: null, label: "Read only", detail: "SELECT", write: false },
  { value: "insert", label: "Read + Insert", detail: "SELECT + INSERT", write: true },
  { value: "update", label: "Read + Update", detail: "SELECT + UPDATE", write: true },
  { value: "write", label: "Read & write", detail: "insert, update, delete", write: true },
];

/** Memory variable permission levels. `null` = both (default). */
export const MEMORY_ACCESS_LEVELS: {
  value: MemoryModifier | null;
  label: string;
  detail: string;
}[] = [
  { value: null, label: "Read & Write", detail: "read + write (default)" },
  { value: "read", label: "Read only", detail: "agent can read this variable" },
  { value: "write", label: "Write only", detail: "agent can write this variable" },
];

/** How not-yet-confirmed (temporary) values are handled. `null` = permanent (default). */
export const MEMORY_TEMP_LEVELS: {
  value: MemoryTempModifier | null;
  label: string;
  detail: string;
}[] = [
  { value: null, label: "Permanent only", detail: "wait for confirmed values (default)" },
  { value: "use", label: "Use temporary", detail: "use unconfirmed values directly" },
];

/** Where this variable's memory lives. `null` = private (default). */
export const MEMORY_SCOPE_LEVELS: {
  value: MemoryScopeModifier | null;
  label: string;
  detail: string;
}[] = [
  { value: null, label: "Private", detail: "the agent's own memory (default)" },
  { value: "workspace", label: "Workspace", detail: "the shared workspace pool" },
];

/**
 * Live chip for a `#kind/slug` mention. Colour reflects the resolution state
 * pulled from context (✅ resolved / ⚠ needs setup / ❌ broken). Table chips are
 * interactive: clicking opens the permission ladder (Read / +Insert / +Update /
 * Read&write); escalating to a write level asks for confirmation, then rewrites
 * the token's `:modifier` via `updateAttributes` (narrative §4 escalation).
 */
export function MentionChip(props: NodeViewProps) {
  const { kind, slug, modifier, tempModifier, scopeModifier, autoOpen } = props.node.attrs as {
    kind: MentionKind;
    slug: string;
    modifier: TableModifier | MemoryModifier | null;
    tempModifier: MemoryTempModifier | null;
    scopeModifier: MemoryScopeModifier | null;
    autoOpen?: boolean;
  };
  const resolutions = useContext(MentionResolutionsContext);
  const res: MentionResolution | undefined = resolutions.get(tokenOf(kind, slug));
  // The resolver returns an entry for every token (broken ones included), so a
  // missing entry means resolution hasn't returned yet — a load/debounce window,
  // not a broken mention. Render it neutrally instead of flashing red ❌.
  const pending = res === undefined;
  const state = pending ? "resolving" : res.state;
  const label = res?.displayName ?? slug;
  const isWrite = kind === "table" ? modifier !== null : kind === "memory" && modifier === "write";
  const isReadOnly = kind === "memory" && modifier === "read";

  // Multi-action tool (composio/native) chips open an action on/off dialog.
  const skillActionsCtx = useContext(SkillActionsContext);
  const isSkill = kind === "skill" || kind === "tool";
  const skillActions: SkillActionOption[] = isSkill
    ? (skillActionsCtx?.getActions(slug) ?? [])
    : [];
  const hasSkillActions = skillActions.length > 0;
  const skillSelected = isSkill ? skillActionsCtx?.getSelected(slug) : undefined;
  // `undefined` selection ⇒ all enabled (the publish default).
  const skillSelectedCount = skillSelected ? skillSelected.length : skillActions.length;

  // request_decision chips open a decision-policy dialog (continue/hold + copy).
  const skillType = isSkill ? skillActionsCtx?.getSkillType?.(slug) : undefined;
  const isDecision = skillType === "request_decision";
  const decisionConfig = isDecision ? skillActionsCtx?.getConfig?.(slug) : undefined;
  const decisionBehavior = decisionConfig?.pendingBehavior ?? "continue";

  const interactive =
    !pending &&
    props.editor.isEditable &&
    (kind === "table" ||
      kind === "memory" ||
      (isDecision && !skillActionsCtx?.readOnly) ||
      (hasSkillActions && !skillActionsCtx?.readOnly));
  const [open, setOpen] = useState(Boolean(autoOpen) && (kind === "table" || kind === "memory"));

  useEffect(() => {
    if (autoOpen) props.updateAttributes({ autoOpen: false });
  }, []);

  const kindIcons = useContext(KindIconsContext);
  const Icon = (kindIcons?.[kind] ?? KIND_ICON[kind]) as ComponentType<{
    className?: string;
    variant?: string;
  }>;
  const chip = (
    <span
      data-state={pending ? "pending" : state}
      data-kind={kind}
      data-modifier={modifier ?? "select"}
      data-testid={`mention-chip-${slug}`}
      title={
        state === "resolving"
          ? "Resolving…"
          : state === "needs_setup"
            ? (res?.needsSetup?.reason ?? "Needs setup")
            : state === "broken"
              ? res?.brokenReason === "malformed"
                ? `Malformed mention "${slug}".`
                : `${KIND_NOUN[kind]} "${slug}" not found. It may have been deleted or renamed.`
              : kind === "table" && interactive
                ? "Click to set table access"
                : kind === "memory" && interactive
                  ? "Click to set memory access"
                  : isSkill && interactive
                    ? "Click to choose which actions this tool can use"
                    : label
      }
      className={cn(
        "inline-block whitespace-nowrap rounded-xs px-1 py-[3px] align-baseline text-[13px] font-normal leading-none select-none",
        interactive && "cursor-pointer",
        state === "resolving" &&
          "bg-[color:color-mix(in_oklch,var(--color-fg)_6%,transparent)] text-muted-foreground animate-pulse",
        state === "resolved" &&
          "bg-[color:color-mix(in_oklch,var(--color-fg)_6%,transparent)] text-foreground",
        state === "needs_setup" &&
          "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
        !pending &&
          state === "broken" &&
          "bg-red-100 text-red-900 line-through dark:bg-red-950 dark:text-red-200",
      )}
      contentEditable={false}
    >
      <Icon
        className="mr-1 inline-block h-3.5 w-3.5 align-middle text-muted-foreground"
        aria-hidden
      />
      <span className="align-baseline">{label}</span>
      {isWrite ? (
        <span className="ml-1 inline-block align-middle" aria-hidden title={`write: ${modifier}`}>
          ✎
        </span>
      ) : isReadOnly ? (
        <span className="ml-1 inline-block align-middle" aria-hidden title="read only">
          👁
        </span>
      ) : null}
      {hasSkillActions ? (
        <span
          className="ml-1 inline-block align-middle font-mono text-[10px] text-muted-foreground"
          title={`${skillSelectedCount} of ${skillActions.length} actions enabled`}
        >
          {skillSelectedCount}/{skillActions.length}
        </span>
      ) : null}
      {isDecision ? (
        <span
          className="font-mono text-[10px] text-muted-foreground"
          title={
            decisionBehavior === "hold"
              ? "Holds the AI until the team rules"
              : "AI keeps talking while it waits"
          }
        >
          {decisionBehavior === "hold" ? "hold" : "continue"}
        </span>
      ) : null}
      {!pending && state === "needs_setup" ? (
        <span className="ml-1 inline-block align-middle" aria-hidden>
          ⚠
        </span>
      ) : null}
      {!pending && state === "broken" ? (
        <span className="ml-1 inline-block align-middle" aria-hidden>
          ❌
        </span>
      ) : null}
      {kind === "memory" && interactive ? (
        <ChevronDown
          className="ml-1 inline-block h-3 w-3 align-middle text-muted-foreground"
          aria-hidden
        />
      ) : null}
    </span>
  );

  if (!interactive) {
    return (
      <NodeViewWrapper as="span" className="cc-mention-chip-wrap">
        {chip}
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper as="span" className="cc-mention-chip-wrap">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <span
            role="button"
            tabIndex={0}
            // Stop ProseMirror's editor-level mousedown handler (atom-node
            // selection / blur) from swallowing the click before Radix opens.
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
            className="inline-flex align-baseline"
          >
            {chip}
          </span>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="max-h-[min(70vh,28rem)] w-72 overflow-y-auto p-0"
          data-testid="chip-access-popover"
        >
          {isDecision ? (
            <DecisionPolicyMenu
              tool={label}
              config={decisionConfig}
              onApply={(next) => skillActionsCtx?.setConfig?.(slug, next)}
            />
          ) : isSkill ? (
            <SkillActionsMenu
              tool={label}
              actions={skillActions}
              // undefined selection ⇒ all enabled by default.
              selected={skillSelected ?? skillActions.map((a) => a.slug)}
              onApply={(next) => skillActionsCtx?.setSelected(slug, next)}
            />
          ) : kind === "memory" ? (
            <MemoryAccessMenu
              variable={label}
              current={modifier as MemoryModifier | null}
              currentTemp={tempModifier}
              currentScope={scopeModifier}
              onApply={(next) => props.updateAttributes({ modifier: next })}
              onApplyTemp={(next) => props.updateAttributes({ tempModifier: next })}
              onApplyScope={(next) => props.updateAttributes({ scopeModifier: next })}
            />
          ) : (
            <TableAccessMenu
              table={label}
              current={modifier as TableModifier | null}
              onApply={(next) => {
                props.updateAttributes({ modifier: next });
                setOpen(false);
              }}
            />
          )}
        </PopoverContent>
      </Popover>
    </NodeViewWrapper>
  );
}

function SkillActionsMenu({
  tool,
  actions,
  selected,
  onApply,
}: {
  tool: string;
  actions: SkillActionOption[];
  selected: string[];
  onApply: (next: string[]) => void;
}) {
  const selectedSet = new Set(selected);
  const allOn = actions.every((a) => selectedSet.has(a.slug));

  const toggle = (slug: string) => {
    const next = new Set(selectedSet);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    // Preserve the tool's action order for a stable token/snapshot.
    onApply(actions.filter((a) => next.has(a.slug)).map((a) => a.slug));
  };

  return (
    <div role="menu" data-testid="chip-skill-actions">
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <span className="font-mono text-xs text-muted-foreground">{tool}</span>
        <button
          type="button"
          className="text-xs font-medium text-primary hover:underline"
          onClick={() => onApply(allOn ? [] : actions.map((a) => a.slug))}
        >
          {allOn ? "Disable all" : "Enable all"}
        </button>
      </div>
      <div className="max-h-64 overflow-y-auto p-1">
        {actions.map((a) => {
          const on = selectedSet.has(a.slug);
          return (
            <button
              key={a.slug}
              type="button"
              role="menuitemcheckbox"
              aria-checked={on}
              data-testid={`chip-skill-action-${a.slug}`}
              onClick={() => toggle(a.slug)}
              className="flex w-full items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-medium">{a.name}</span>
                <span className="truncate font-mono text-[11px] text-muted-foreground">
                  {a.slug}
                </span>
              </span>
              <span
                aria-hidden
                className={cn(
                  "flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px]",
                  on ? "border-primary bg-primary text-primary-foreground" : "border-input",
                )}
              >
                {on ? "✓" : ""}
              </span>
            </button>
          );
        })}
      </div>
      <p className="border-t px-3 py-2 text-[11px] text-muted-foreground">
        Applies when you Publish.
      </p>
    </div>
  );
}

/** continue vs hold, with the plain-language trade-off. */
const DECISION_BEHAVIORS: {
  value: "continue" | "hold";
  label: string;
  detail: string;
}[] = [
  {
    value: "continue",
    label: "Keep chatting",
    detail: "AI keeps helping and shares the ruling when it lands",
  },
  {
    value: "hold",
    label: "Pause the AI",
    detail: "AI stops until the team rules, then announces it",
  },
];

/**
 * Decision-policy dialog for a `#request_decision` chip (M9 §2.5). Edits the
 * per-agent policy staged in the agent form and applied to the agent's
 * `DecisionType` on publish. Local draft state keeps inputs focused across the
 * form re-renders each keystroke triggers.
 */
function DecisionPolicyMenu({
  tool,
  config,
  onApply,
}: {
  tool: string;
  config: DecisionPolicyConfig | undefined;
  onApply: (next: DecisionPolicyConfig) => void;
}) {
  const [draft, setDraft] = useState<DecisionPolicyConfig>({
    pendingBehavior: config?.pendingBehavior ?? "continue",
    holdMessage: config?.holdMessage ?? "",
    guidance: config?.guidance ?? "",
    subjectHint: config?.subjectHint ?? "",
  });
  const update = (patch: Partial<DecisionPolicyConfig>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    onApply(next);
  };
  const inputCls =
    "w-full rounded border border-input bg-transparent px-2 py-1 text-sm outline-none focus:border-primary";

  return (
    <div role="menu" data-testid="chip-decision-policy">
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <span className="font-mono text-xs text-muted-foreground">{tool}</span>
      </div>

      <div className="p-1">
        <p className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          While waiting for the team
        </p>
        {DECISION_BEHAVIORS.map((b) => {
          const active = b.value === draft.pendingBehavior;
          return (
            <button
              key={b.value}
              type="button"
              role="menuitemradio"
              aria-checked={active}
              data-testid={`chip-decision-behavior-${b.value}`}
              onClick={() => update({ pendingBehavior: b.value })}
              className={cn(
                "flex w-full items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent",
                active && "bg-accent",
              )}
            >
              <span className="flex flex-col">
                <span className="font-medium">{b.label}</span>
                <span className="text-xs text-muted-foreground">{b.detail}</span>
              </span>
              {active ? <span aria-hidden>✓</span> : null}
            </button>
          );
        })}
      </div>

      {draft.pendingBehavior === "hold" ? (
        <div className="space-y-1 border-t px-3 py-2">
          <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Message while paused
          </label>
          <textarea
            rows={2}
            data-testid="chip-decision-hold-message"
            className={inputCls}
            placeholder="Let me check with my team and get right back to you."
            value={draft.holdMessage ?? ""}
            onChange={(e) => update({ holdMessage: e.target.value })}
          />
        </div>
      ) : null}

      <div className="space-y-1 border-t px-3 py-2">
        <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          When to ask
        </label>
        <textarea
          rows={2}
          data-testid="chip-decision-guidance"
          className={inputCls}
          placeholder="e.g. any discount above 10%, or a refund over $100"
          value={draft.guidance ?? ""}
          onChange={(e) => update({ guidance: e.target.value })}
        />
      </div>

      <div className="space-y-1 border-t px-3 py-2">
        <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Applies to (optional)
        </label>
        <input
          type="text"
          data-testid="chip-decision-subject"
          className={inputCls}
          placeholder="e.g. order reference"
          value={draft.subjectHint ?? ""}
          onChange={(e) => update({ subjectHint: e.target.value })}
        />
      </div>

      <p className="border-t px-3 py-2 text-[11px] text-muted-foreground">
        Applies when you Publish.
      </p>
    </div>
  );
}

function MemoryAccessMenu({
  variable,
  current,
  currentTemp,
  currentScope,
  onApply,
  onApplyTemp,
  onApplyScope,
}: {
  variable: string;
  current: MemoryModifier | null;
  currentTemp: MemoryTempModifier | null;
  currentScope: MemoryScopeModifier | null;
  onApply: (next: MemoryModifier | null) => void;
  onApplyTemp: (next: MemoryTempModifier | null) => void;
  onApplyScope: (next: MemoryScopeModifier | null) => void;
}) {
  return (
    <div role="menu" className="py-1">
      <MemoryGroup label="Access">
        {MEMORY_ACCESS_LEVELS.map((lvl) => (
          <MemoryRow
            key={lvl.value ?? "both"}
            active={lvl.value === current}
            onClick={() => onApply(lvl.value)}
            label={lvl.label}
            detail={lvl.detail}
            testId={`chip-memory-level-${lvl.value ?? "both"}`}
          />
        ))}
      </MemoryGroup>
      <MemoryGroup label="Temporary values">
        {MEMORY_TEMP_LEVELS.map((lvl) => {
          // null and explicit "permanent" both mean the permanent (default) option.
          const active =
            lvl.value === currentTemp || (lvl.value === null && currentTemp === "permanent");
          return (
            <MemoryRow
              key={lvl.value ?? "permanent"}
              active={active}
              onClick={() => onApplyTemp(lvl.value)}
              label={lvl.label}
              detail={lvl.detail}
              testId={`chip-memory-temp-${lvl.value ?? "permanent"}`}
            />
          );
        })}
      </MemoryGroup>
      <MemoryGroup label="Scope">
        {MEMORY_SCOPE_LEVELS.map((lvl) => {
          // null and explicit "private" both mean the private (default) option.
          const active =
            lvl.value === currentScope || (lvl.value === null && currentScope === "private");
          return (
            <MemoryRow
              key={lvl.value ?? "private"}
              active={active}
              onClick={() => onApplyScope(lvl.value)}
              label={lvl.label}
              detail={lvl.detail}
              testId={`chip-memory-scope-${lvl.value ?? "private"}`}
            />
          );
        })}
      </MemoryGroup>
    </div>
  );
}

/** Section group in the memory chip dropdown — normal-case section header
 *  followed by its radio rows, with a separator above (except the first). */
function MemoryGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="px-1 [&:not(:first-child)]:mt-1 [&:not(:first-child)]:border-t [&:not(:first-child)]:border-dashed [&:not(:first-child)]:pt-1">
      <p className="px-2 py-1 text-[11px] font-medium text-muted-foreground">{label}</p>
      {children}
    </div>
  );
}

/** Single radio row — a filled-dot indicator on the LEFT (instead of a check
 *  on the right) mirrors the Storybook Final dropdown. */
function MemoryRow({
  active,
  onClick,
  label,
  detail,
  testId,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  detail: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      data-testid={testId}
      onClick={onClick}
      className="flex w-full items-start gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted"
    >
      <span
        aria-hidden
        className={cn(
          "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
          active ? "border-[color:var(--gray-900)]" : "border-input",
        )}
      >
        {active ? <span className="h-2 w-2 rounded-full bg-[color:var(--gray-900)]" /> : null}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="text-[13px] font-medium text-[color:var(--gray-900)]">{label}</span>
        <span className="text-xs text-[color:var(--color-fg-subtle)]">{detail}</span>
      </span>
    </button>
  );
}

function TableAccessMenu({
  table,
  current,
  onApply,
}: {
  table: string;
  current: TableModifier | null;
  onApply: (next: TableModifier | null) => void;
}) {
  // A write level is a deliberate escalation: stage it, confirm, then apply.
  const [pending, setPending] = useState<TableModifier | null | undefined>(undefined);

  const pick = (level: TableModifier | null, write: boolean) => {
    if (level === current) return;
    if (write) setPending(level);
    else onApply(level);
  };

  if (pending !== undefined) {
    return (
      <div className="space-y-3 p-3" data-testid="chip-access-confirm">
        <p className="text-sm">
          This agent will be able to <strong>modify data</strong> in{" "}
          <span className="font-mono">{table}</span>
          {pending === "write" ? " — including deleting rows." : "."}
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md px-2.5 py-1.5 text-sm hover:bg-accent"
            onClick={() => setPending(undefined)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-md bg-primary px-2.5 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            data-testid="chip-grant-confirm"
            onClick={() => onApply(pending)}
          >
            Grant access
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-1" role="menu">
      <p className="px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Table access
      </p>
      {TABLE_ACCESS_LEVELS.map((lvl) => {
        const active = lvl.value === current;
        return (
          <button
            key={lvl.value ?? "select"}
            type="button"
            role="menuitemradio"
            aria-checked={active}
            data-testid={`chip-level-${lvl.value ?? "read"}`}
            onClick={() => pick(lvl.value, lvl.write)}
            className={cn(
              "flex w-full items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent",
              active && "bg-accent",
            )}
          >
            <span className="flex flex-col">
              <span className="font-medium">{lvl.label}</span>
              <span className="font-mono text-xs text-muted-foreground">{lvl.detail}</span>
            </span>
            {active ? <span aria-hidden>✓</span> : null}
          </button>
        );
      })}
    </div>
  );
}
